/**
 * stageFlow — pure stage-progression routing logic.
 *
 * No Phaser import. Determines which Phaser scene key (and with what init
 * data) to transition to at each decision point in the run flow.
 *
 * Callers supply the cutscene-existence flags so this module stays
 * independent of the cutscene registry and fully testable in Node.
 */
import type { GameMode } from '@/state/GameState';

export interface RouteResult {
  /** Phaser scene key to pass to scene.start(). */
  sceneKey: string;
  /** Init data passed to scene.start(). Absent when not needed. */
  data?: Record<string, unknown>;
}

/**
 * Route immediately after a stage clears (called from StageScene).
 *
 * @param stage          Stage index that was just cleared (1-indexed).
 * @param hasEndCutscene Whether a `stage{n}-end` entry exists in the registry.
 * @param _isFinal       Whether this was the final stage (reserved for future credits routing).
 */
export function nextRoute(stage: number, hasEndCutscene: boolean, _isFinal: boolean): RouteResult {
  if (hasEndCutscene) {
    return {
      sceneKey: 'EndCutsceneScene',
      data: { cutsceneId: `stage${stage}-end`, nextSceneKey: 'ResultsScene' },
    };
  }
  return { sceneKey: 'ResultsScene' };
}

/**
 * Route when the player confirms "continue" from ResultsScene (or from
 * CharSelectScene on a fresh/resumed run start).
 *
 * @param nextStage        Stage index the player is about to enter.
 * @param hasIntroCutscene Whether a `stage{n}-intro` entry exists in the registry.
 */
export function nextStageRoute(nextStage: number, hasIntroCutscene: boolean): RouteResult {
  if (hasIntroCutscene) {
    return {
      sceneKey: 'IntroCutsceneScene',
      data: { cutsceneId: `stage${nextStage}-intro`, nextSceneKey: 'StageScene' },
    };
  }
  return { sceneKey: 'StageScene' };
}

/** Stage at which Halloway's deal gate fires (before entering the finale). */
export const DEAL_TRIGGER_STAGE = 9;

// ── Mode-aware routing ────────────────────────────────────────────────────────

/**
 * Returns the scene to route to when starting a run, based on the selected
 * game mode. Campaign falls through to the normal CharSelect → Stage flow.
 *
 * @param mode     The selected game mode.
 * @param hasIntro Whether a `stage1-intro` cutscene exists (campaign only).
 */
export function modeStartRoute(mode: GameMode, hasIntro: boolean): RouteResult {
  if (mode === 'boss-rush' || mode === 'score-attack' || mode === 'survival') {
    return { sceneKey: 'StageScene', data: { gameMode: mode } };
  }
  // campaign — normal stage 1 entry
  return nextStageRoute(1, hasIntro);
}

/**
 * Route to loop back into the arena after a wave clears in Survival mode.
 * StageScene calls this after each wave + wave-clear bonus award. GameState
 * .survivalWave has already been incremented by the caller before this.
 */
export function survivalWaveRoute(): RouteResult {
  return { sceneKey: 'StageScene', data: { gameMode: 'survival' } };
}

/**
 * Route when the player is KO'd with no lives remaining in Survival mode.
 * Routes to ResultsScene so the run summary + high-score banner can show.
 */
export function survivalGameOverRoute(): RouteResult {
  return { sceneKey: 'ResultsScene', data: { gameMode: 'survival' } };
}

/**
 * Route after a boss is defeated in Boss Rush mode.
 *
 * @param bossesDefeated Total bosses beaten so far (including the one just defeated).
 * @param totalBosses    Length of the boss sequence (typically 9).
 */
export function bossRushNextRoute(bossesDefeated: number, totalBosses: number): RouteResult {
  if (bossesDefeated >= totalBosses) {
    return { sceneKey: 'ResultsScene', data: { gameMode: 'boss-rush' } };
  }
  return { sceneKey: 'StageScene', data: { gameMode: 'boss-rush', bossRushIndex: bossesDefeated } };
}

/**
 * Route after a stage clears in Score Attack mode.
 * Score Attack plays all 9 stages; on the final clear it routes to Results.
 *
 * @param stage      Stage just cleared (1-indexed).
 * @param finalStage The last stage number (typically 9).
 */
export function scoreAttackNextRoute(stage: number, finalStage: number): RouteResult {
  if (stage >= finalStage) {
    return { sceneKey: 'ResultsScene', data: { gameMode: 'score-attack' } };
  }
  return { sceneKey: 'StageScene', data: { gameMode: 'score-attack' } };
}

/**
 * Returns true when the progression should pause for Halloway's deal scene
 * rather than routing directly to the next stage.
 */
export function shouldShowDeal(nextStage: number): boolean {
  return nextStage === DEAL_TRIGGER_STAGE;
}

/**
 * Route into DealScene, playing the 'halloway-deal' cutscene first if registered.
 *
 * @param hasDealIntroCutscene  Whether 'halloway-deal' exists in the registry
 */
export function dealIntroRoute(hasDealIntroCutscene: boolean): RouteResult {
  if (hasDealIntroCutscene) {
    return {
      sceneKey: 'IntroCutsceneScene',
      data: { cutsceneId: 'halloway-deal', nextSceneKey: 'DealScene' },
    };
  }
  return { sceneKey: 'DealScene' };
}

/**
 * Route after the player picks a PM fate ending in PMFateScene (MY-1979).
 *
 * @param endingId       The selected ending id (e.g. 'peoples-verdict').
 * @param hasEpiCutscene Whether 'ending-{endingId}' is registered in the cutscene registry.
 */
export function pmFateRoute(endingId: string, hasEpiCutscene: boolean): RouteResult {
  const cutsceneId = `ending-${endingId}`;
  if (hasEpiCutscene) {
    return {
      sceneKey: 'EndCutsceneScene',
      data: { cutsceneId, nextSceneKey: 'CreditsScene', nextData: { endingId } },
    };
  }
  return { sceneKey: 'CreditsScene', data: { endingId } };
}

/**
 * Route after the player makes a choice in DealScene.
 *
 * State mutations (tookDeal increment, stage reset) are handled by the caller
 * (DealScene) before this is called — this fn is pure routing logic only.
 *
 * @param choice                'refuse' or 'accept'
 * @param tookDeal              Times the player previously accepted (0 or 1 before this choice)
 * @param hasStage9Intro        Whether a 'stage9-intro' cutscene exists in the registry
 * @param hasBetrayalCutscene   Whether a 'halloway-betrayal' cutscene exists in the registry
 * @param hasSelloutCutscene    Whether a 'ending-sellout' cutscene exists in the registry
 */
export function dealRoute(
  choice: 'refuse' | 'accept',
  tookDeal: number,
  hasStage9Intro: boolean,
  hasBetrayalCutscene: boolean,
  hasSelloutCutscene = false,
): RouteResult {
  if (choice === 'refuse') {
    if (hasStage9Intro) {
      return {
        sceneKey: 'IntroCutsceneScene',
        data: { cutsceneId: 'stage9-intro', nextSceneKey: 'StageScene' },
      };
    }
    return { sceneKey: 'StageScene' };
  }

  // ACCEPT — second time → sell-out BAD ENDING
  if (tookDeal >= 1) {
    if (hasSelloutCutscene) {
      return {
        sceneKey: 'EndCutsceneScene',
        data: {
          cutsceneId: 'ending-sellout',
          nextSceneKey: 'CreditsScene',
          nextData: { variant: 'sellout' },
        },
      };
    }
    return { sceneKey: 'CreditsScene', data: { variant: 'sellout' } };
  }

  // ACCEPT — first time → betrayal cutscene then loop back to stage 6
  if (hasBetrayalCutscene) {
    return {
      sceneKey: 'IntroCutsceneScene',
      data: { cutsceneId: 'halloway-betrayal', nextSceneKey: 'StageScene' },
    };
  }
  return { sceneKey: 'StageScene' };
}
